import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../Redux/hooks";
import { fetchAdSlots, fetchAdSlotsBatch } from "../Redux/storefront-ads/slice";
import type {
  AdPlacement,
  AdType,
  FetchAdSlotsBatchParams,
  FetchAdSlotsParams,
} from "../Redux/storefront-ads/types";
import type { SponsoredAdSlot } from "../Redux/ecommersProduct/types";

type UseAdSlotsOptions = {
  placement: AdPlacement;
  adTypes?: AdType[];
  limit?: number;
  searchQuery?: string;
  categoryType?: string;
  productId?: string;
  enabled?: boolean;
};

const useDeliveryContext = () => {
  const { location } = useAppSelector((state) => state.deliveryLocation);
  const { user } = useAppSelector((state) => state.customerAuth);

  return useMemo(
    () => ({
      city: location?.city || location?.area || "all",
      pincode: location?.pincode,
      userId: user?._id,
    }),
    [location, user],
  );
};

export const useAdSlots = ({
  placement,
  adTypes,
  limit,
  searchQuery,
  categoryType,
  productId,
  enabled = true,
}: UseAdSlotsOptions) => {
  const dispatch = useAppDispatch();
  const { city, pincode, userId } = useDeliveryContext();
  const slots = useAppSelector(
    (state) => state.storefrontAds.slotsByPlacement[placement] ?? [],
  );
  const loading = useAppSelector(
    (state) => state.storefrontAds.loading[placement] ?? false,
  );

  // Serialize adTypes so a new array literal from the caller on every render
  // doesn't retrigger the effect (which would loop: dispatch → state change →
  // re-render → new array reference → dispatch again).
  const adTypesKey = adTypes ? adTypes.join(",") : "";

  useEffect(() => {
    if (!enabled) return;

    const params: FetchAdSlotsParams = {
      placement,
      city,
      pincode,
      userId,
      adTypes: adTypesKey ? (adTypesKey.split(",") as AdType[]) : undefined,
      limit,
      searchQuery,
      categoryType,
      productId,
    };

    dispatch(fetchAdSlots(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    enabled,
    placement,
    city,
    pincode,
    userId,
    adTypesKey,
    limit,
    searchQuery,
    categoryType,
    productId,
  ]);

  const byType = useMemo(() => {
    const grouped: Record<string, SponsoredAdSlot[]> = {
      product: [],
      sponsored_brand: [],
      display: [],
    };
    for (const slot of slots) {
      if (grouped[slot.adType]) grouped[slot.adType].push(slot);
    }
    return grouped;
  }, [slots]);

  return { slots, byType, loading };
};

export const useAdSlotsBatch = (
  placements: FetchAdSlotsBatchParams["placements"],
  enabled = true,
) => {
  const dispatch = useAppDispatch();
  const { city, pincode } = useDeliveryContext();
  const slotsByPlacement = useAppSelector(
    (state) => state.storefrontAds.slotsByPlacement,
  );

  // Serialize placements so a new array literal each render doesn't loop.
  const placementsKey = JSON.stringify(placements);

  useEffect(() => {
    if (!enabled || placements.length === 0) return;
    dispatch(fetchAdSlotsBatch({ city, pincode, placements }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enabled, city, pincode, placementsKey]);

  return { slotsByPlacement };
};

export default useAdSlots;
